import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";

export default function VerifyEmailPage() {
  const [location] = useLocation();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();

  const token = new URLSearchParams(window.location.search).get("token");

  const [status, setStatus] = useState<"loading" | "success" | "error" | "idle">(
    token ? "loading" : "idle"
  );
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) return;
    fetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setStatus("success");
          setMessage(data.message || "Email verified successfully");
          // Refresh the auth cache so the verification banner disappears immediately
          queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        } else {
          setStatus("error");
          setMessage(data.message || "Verification failed");
        }
      })
      .catch(() => {
        setStatus("error");
        setMessage("Something went wrong. Please try again.");
      });
  }, [token]);

  const resendMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/auth/resend-verification"),
    onSuccess: () => toast({ title: "Verification email sent — please check your inbox" }),
    onError: (err: any) => toast({
      title: err?.message || "Failed to resend verification email",
      variant: "destructive",
    }),
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === "loading" && (
            <>
              <div className="flex justify-center mb-4">
                <Loader2 className="h-12 w-12 text-primary animate-spin" />
              </div>
              <CardTitle>Verifying your email…</CardTitle>
              <CardDescription>Just a moment</CardDescription>
            </>
          )}
          {status === "success" && (
            <>
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
              <CardTitle>Email verified</CardTitle>
              <CardDescription>{message}</CardDescription>
            </>
          )}
          {status === "error" && (
            <>
              <div className="flex justify-center mb-4">
                <XCircle className="h-12 w-12 text-destructive" />
              </div>
              <CardTitle>Verification failed</CardTitle>
              <CardDescription>{message}</CardDescription>
            </>
          )}
          {status === "idle" && (
            <>
              <div className="flex justify-center mb-4">
                <Mail className="h-12 w-12 text-primary" />
              </div>
              <CardTitle>Check your email</CardTitle>
              <CardDescription>
                We sent a verification link to your email address. Click the link to verify your account.
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-3">
          {status === "success" && (
            <Button asChild className="w-full" data-testid="button-go-home">
              <a href="/">Go to home page</a>
            </Button>
          )}
          {(status === "error" || status === "idle") && isAuthenticated && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => resendMutation.mutate()}
              disabled={resendMutation.isPending}
              data-testid="button-resend-verification"
            >
              {resendMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Resend verification email
            </Button>
          )}
          <Button variant="ghost" asChild className="w-full" data-testid="button-back-home">
            <a href="/">Back to home page</a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
